import type { MouseEvent } from 'react';
import { useState, useEffect } from 'react';
import { CircuitBoard, Download, Github, Menu, X } from 'lucide-react';
import { Button } from './ui/Button';
import './Header.css';

const GITHUB_REPO = 'https://github.com/IntelligentElectron/universal-netlist';
const GITHUB_RELEASES = `${GITHUB_REPO}/releases`;

const NAV_LINKS = [
  { label: 'Features', href: '#features' },
  { label: 'Formats', href: '#formats' },
  { label: 'Integrations', href: '#integrations' },
];

/**
 * Fixed header with navigation and download CTA.
 */
export function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleNavClick = (e: MouseEvent<HTMLAnchorElement>, href: string) => {
    e.preventDefault();
    setMobileMenuOpen(false);
    const target = document.querySelector(href);
    if (target) {
      const headerHeight = 80;
      const top = target.getBoundingClientRect().top + window.scrollY - headerHeight;
      window.scrollTo({ top, behavior: 'smooth' });
    }
  };

  return (
    <header className={`header ${scrolled ? 'header-scrolled' : ''}`}>
      <div className="header-container">
        <a href="/" className="header-logo">
          <CircuitBoard className="header-logo-icon" />
          <span className="header-logo-text">Intelligent Electron</span>
        </a>

        <nav className={`header-nav ${mobileMenuOpen ? 'header-nav-open' : ''}`}>
          {NAV_LINKS.map((link) => (
            <a
              key={link.href}
              href={link.href}
              className="header-nav-link"
              onClick={(e) => handleNavClick(e, link.href)}
            >
              {link.label}
            </a>
          ))}
          <a
            href={GITHUB_REPO}
            className="header-nav-link header-nav-github"
            target="_blank"
            rel="noopener noreferrer"
          >
            <Github size={18} />
            <span>GitHub</span>
          </a>
        </nav>

        <div className="header-actions">
          <Button variant="primary" size="sm" icon={<Download size={16} />} href={GITHUB_RELEASES}>
            Download
          </Button>

          <button
            className="header-mobile-toggle"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Toggle menu"
          >
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>
    </header>
  );
}
